import {Inject, Injectable} from '@angular/core';
import {apiUrlMap} from '@ox/config';
import {ApiCacheService, BML, replaceVariable} from '@ox/utils';
import {PdpData} from './pdp';
import {
  AEMLayout,
  ConsentData,
  ConsentsInfo,
  CountryValidationInfo,
  BML as iBML,
  ConsentLinks,
  TextConsent, LinkConsent, InterimCatalogDisclosureData, InterimCatalogDisclosure
} from '@ox/typings';
import {BehaviorSubject, map, of, take, tap} from 'rxjs';
import {DomSanitizer} from '@angular/platform-browser';

@Injectable()
export class AemDataService {
  consents = new BehaviorSubject<ConsentData[]>([]);
  consents$ = this.consents.asObservable();

  disclosures = new BehaviorSubject<InterimCatalogDisclosureData[]>([]);
  disclosures$ = this.disclosures.asObservable();

  constructor(private api: ApiCacheService, @Inject(BML) private bml: iBML, private domSanitizer: DomSanitizer) {}

  getDlpJSON(
    brand?: string,
    region?: string,
    lang?: string,
    variation?: string,
    year?: string
  ) {
    const route = apiUrlMap.aemContentStructure.getDlpJSON(
      brand,
      region,
      lang,
      variation,
      year
    );
    return this.api.get<AEMLayout>({tokenOrUrl: route});
  }

  getPdpJSON(
    brand: string,
    product: string,
    region: string,
    lang?: string,
    variation?: string,
    year?: string
  ) {
    const route = apiUrlMap.aemContentStructure.getPdpJSON(
      brand,
      product,
      region,
      lang,
      variation,
      year
    );
    return this.api.get<PdpData>({tokenOrUrl: route});
  }

  filteredAEMConentByType(componentKey: string, JSONFile: any) {
    const debug = JSONFile.filter((obj: any) => obj.type === componentKey);
    return debug;
  }

  getConsentsJSON() {
    const route = apiUrlMap.aemContentStructure.getConsentsJSON(this.bml?.market, this.bml?.lang);
    return this.api.get<ConsentsInfo>({tokenOrUrl: route});
  }

  getAllConsentData() {
    return this.getConsentsJSON()
      .pipe(
          take(1),
          map(({data: {consentData}}) => consentData || []),
          map((consents) => {
            consents.forEach(c => {
              if(c.links) c.linksExpanded = this.splitConsentLink(c.links, c.consentDescription);
            });
            return consents;
          }),
        tap(consents => this.consents.next(consents)));
  }

  getDisclosureJSON() {
    const route = apiUrlMap.aemContentStructure.getDisclosuresJSON(this.bml?.market, this.bml?.lang);
    return this.api.get<InterimCatalogDisclosure>({tokenOrUrl: route});
  }

  getAllDisclosureData() {
    return this.getDisclosureJSON().pipe(
      take(1),
      map(({data: {disclosureData}}) => disclosureData || []),
      tap((disclosures) => {
        this.disclosures.next(disclosures);
      })
    );
  }

  getConsentByKey(key: string) {
    return this.consents$.pipe(
      map((consents) => consents.find(c => c.consentKey === key) || {} as ConsentData)
    );
  }

  getConsentDescriptionByKey(key: string) {
    return this.consents$.pipe(
      map((consents) => consents.find(c => c.consentKey === key) || {} as ConsentData),
      map(({consentDescription}) => this.domSanitizer.bypassSecurityTrustHtml(consentDescription) as string ?? '')
    );
  }

  getDisclosureDescriptionByKey(key: string | null) {
    if(!key) return of('');
    else {
      return this.disclosures$.pipe(
      map((disclosures) => disclosures.find(d => d.disclosureKey === key) || {} as InterimCatalogDisclosureData),
      map(({disclosureDescription}) =>
        this.domSanitizer.bypassSecurityTrustHtml(replaceVariable(disclosureDescription, {Brand: this.bml.brand})) as string ?? '')
      );
    }  
  }

  getCountryValidationJSON() {
    const route = apiUrlMap.aemContentStructure.getCountryValidationJSON(this.bml?.market, this.bml?.lang);
    return this.api.get<CountryValidationInfo>({tokenOrUrl: route});
  }

  private splitConsentLink(links: ConsentLinks[], text: string) {
    const arr = new Array<LinkConsent | TextConsent>;
    const newTexts = text.replace(/{{([a-z0-9-_]+)}}/ig, '::$1::').split('::');
    newTexts.forEach((text, key) => {
        if (key % 2 === 0) arr.push({link: false, text} as TextConsent); else {
            const linkData = this.getLinkData(text, links);
            if (linkData) arr.push(linkData);
        }
    });
    return arr;
  }

  private getLinkData(id: string, links: ConsentLinks[]) {
    if (!links.some(l => l.placeholder === id)) return {link: false, text: `{{${id}}}`, injection: true} as TextConsent;
    const {url = '', displayText = ''} = links.find(l => l.placeholder === id) as ConsentLinks;
    return {
        link: true, placeholder: id, url: url, text: displayText
    } as LinkConsent;
  }
}
